import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ActionIcon,
  Alert,
  Anchor,
  Button,
  Card,
  Code,
  Group,
  Loader,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import {
  IconAlertTriangle,
  IconApps,
  IconCloudUpload,
  IconEye,
  IconEyeOff,
  IconExternalLink,
  IconLock,
  IconPlayerPlay,
  IconPlayerStop,
  IconRefresh,
  IconTerminal2,
  IconTrash,
} from "@tabler/icons-react";
import { apiDelete, apiFetch, apiPost, friendlyError, type ApiError } from "../../api/client.js";
import { StatusPill } from "../../components/StatusPill.js";
import { isApiError } from "./BackupsCard.js";
import {
  APP_TERMINAL_STATUSES,
  appActionError,
  appStatusCopy,
  type AppInstanceDetail as AppInstanceDetailData,
  type CatalogAppDetail,
} from "./appUtils.js";

const POLL_MS = 5000;
const LOGS_TAIL = 200;

const dtFmt = new Intl.DateTimeFormat(undefined, {
  dateStyle: "medium",
  timeStyle: "short",
  timeZone: "Pacific/Auckland",
});

/** Backend timestamps come from SQLite `datetime('now')` — "YYYY-MM-DD
 *  HH:MM:SS" in UTC with no zone marker. Normalize to ISO-UTC so the
 *  browser doesn't local-parse the wall clock. */
function parseBackendTime(raw: string): Date | null {
  const s = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(raw)
    ? `${raw.replace(" ", "T")}Z`
    : raw;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDateTime(raw: string | null | undefined): string {
  if (!raw) return "never";
  const d = parseBackendTime(raw);
  return d ? dtFmt.format(d) : raw;
}

export function AppInstanceDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [instance, setInstance] = useState<AppInstanceDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<Set<number>>(new Set());
  const [revealed, setRevealed] = useState<Set<string>>(new Set());
  // RAW variable values, lazily fetched from the reveal endpoint the first
  // time a server-masked (password-type) row is revealed. Null until then.
  const [rawVars, setRawVars] = useState<Record<string, string> | null>(null);
  const [varLabels, setVarLabels] = useState<Record<string, string> | null>(null);
  const [logs, setLogs] = useState<string | null>(null);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState<string | null>(null);
  const prevStatusRef = useRef<string | null>(null);
  // Token guarding loadLogs against id changes mid-flight (stale logs race).
  const logsReqRef = useRef(0);
  // Token guarding the reveal fetch against id changes mid-flight.
  const rawVarsReqRef = useRef(0);

  async function load() {
    if (!id) return;
    try {
      const data = await apiFetch<AppInstanceDetailData>(`/api/apps/instances/${id}`);
      const prev = prevStatusRef.current;
      prevStatusRef.current = data.status;
      if (prev === "creating" && data.status === "running") {
        notifications.show({
          color: "green",
          title: "App is live",
          message: "Your app is up and running at its address.",
        });
      }
      if (prev === "updating" && data.status === "running") {
        notifications.show({
          color: "green",
          title: "App updated",
          message: "Your app finished updating and is running the latest version.",
        });
      }
      setInstance(data);
      setError(null);
    } catch (err) {
      const apiErr = err as ApiError;
      if (apiErr.status === 404 && instance !== null) {
        // Poll hit a row the sweep just soft-deleted — the app is gone.
        notifications.show({ color: "green", message: "App deleted." });
        navigate("/dashboard/apps", { replace: true });
        return;
      }
      if (apiErr.status === 404) {
        setError("This app doesn't exist or you don't have access to it.");
      } else {
        setError(friendlyError(err, "Could not load this app."));
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    prevStatusRef.current = null;
    logsReqRef.current++;
    rawVarsReqRef.current++;
    setInstance(null);
    setLoading(true);
    setError(null);
    setRevealed(new Set());
    setRawVars(null);
    setLogs(null);
    setLogsLoading(false);
    setLogsError(null);
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Poll only while a background task owns the row (pending/creating/
  // updating/deleting/degraded); terminal statuses stop the interval
  // entirely — `updating` is non-terminal so the update is followed live.
  const transitional = instance !== null && !APP_TERMINAL_STATUSES.has(instance.status);
  useEffect(() => {
    if (!transitional) return;
    const t = setInterval(() => {
      if (document.visibilityState === "visible") void load();
    }, POLL_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transitional, id]);

  // Human labels for variable keys, from the template's current schema.
  // Unknown template (deleted from catalog) → raw keys still render.
  const templateSlug = instance?.template_slug ?? null;
  useEffect(() => {
    if (!templateSlug) return;
    let cancelled = false;
    apiFetch<CatalogAppDetail>(`/api/apps/catalog/${templateSlug}`)
      .then((tpl) => {
        if (cancelled) return;
        const labels: Record<string, string> = {};
        for (const v of tpl.variables) labels[v.key] = v.label;
        setVarLabels(labels);
      })
      .catch(() => {
        // keep raw keys as labels
      });
    return () => {
      cancelled = true;
    };
  }, [templateSlug]);

  function setInstanceBusy(instId: number, on: boolean) {
    setBusy((prev) => {
      const next = new Set(prev);
      if (on) next.add(instId);
      else next.delete(instId);
      return next;
    });
  }

  async function runAction(action: "start" | "stop" | "restart") {
    if (!instance || busy.has(instance.id)) return;
    const instId = instance.id;
    setInstanceBusy(instId, true);
    try {
      await apiPost<{ status: string }>(`/api/apps/instances/${instId}/${action}`);
      const verb =
        action === "start" ? "starting" : action === "stop" ? "stopping" : "restarting";
      notifications.show({ color: "green", message: `App ${verb}…` });
      await load();
    } catch (err) {
      if (!isApiError(err)) {
        notifications.show({ color: "red", message: "Couldn't reach the server." });
        return;
      }
      const mapped = appActionError(err, "That action failed. Try again in a moment.");
      notifications.show({ color: "red", title: mapped.title, message: mapped.message });
    } finally {
      setInstanceBusy(instId, false);
    }
  }

  function confirmDelete() {
    if (!instance) return;
    modals.openConfirmModal({
      title: "Delete app",
      centered: true,
      closeOnClickOutside: false,
      closeOnEscape: false,
      children: (
        <Text size="sm">
          Permanently delete <b>{instance.name}</b>? Its web address stops working and all
          app data and volumes are permanently destroyed. <b>This cannot be undone.</b>
        </Text>
      ),
      labels: { confirm: "Delete app", cancel: "Cancel" },
      confirmProps: { color: "red" },
      groupProps: { gap: "xs" },
      onConfirm: () => void deleteInstance(),
    });
  }

  async function deleteInstance() {
    if (!instance || busy.has(instance.id)) return;
    const instId = instance.id;
    setInstanceBusy(instId, true);
    try {
      const res = await apiDelete<{ status?: string } | null>(
        `/api/apps/instances/${instId}`
      );
      if (res && res.status === "deleting") {
        notifications.show({
          color: "orange",
          title: "Delete queued",
          message: "Cleaning up in the background — the app will disappear shortly.",
        });
        await load();
      } else {
        notifications.show({ color: "green", message: "App deleted." });
        navigate("/dashboard/apps");
      }
    } catch (err) {
      if (!isApiError(err)) {
        notifications.show({ color: "red", message: "Couldn't reach the server." });
        return;
      }
      const mapped = appActionError(err, "Couldn't delete this app. Try again in a moment.");
      notifications.show({ color: "red", title: mapped.title, message: mapped.message });
    } finally {
      setInstanceBusy(instId, false);
    }
  }

  function confirmUpdate() {
    if (!instance) return;
    modals.openConfirmModal({
      title: "Update app",
      centered: true,
      closeOnClickOutside: false,
      closeOnEscape: false,
      children: (
        <Text size="sm">
          Update <b>{instance.name}</b> from the current template version? New images are
          pulled and the containers are recreated. Your data volumes are preserved, but a
          stopped app will be started, and there is a brief downtime while containers are
          recreated.
        </Text>
      ),
      labels: { confirm: "Update app", cancel: "Cancel" },
      groupProps: { gap: "xs" },
      onConfirm: () => void updateInstance(),
    });
  }

  async function updateInstance() {
    if (!instance || busy.has(instance.id)) return;
    const instId = instance.id;
    setInstanceBusy(instId, true);
    try {
      await apiPost<{ id: number; status: string }>(`/api/apps/instances/${instId}/update`);
      notifications.show({
        color: "green",
        title: "Update started",
        message:
          "Pulling new images and recreating containers — this usually takes a minute or two.",
      });
      await load();
    } catch (err) {
      if (!isApiError(err)) {
        notifications.show({ color: "red", message: "Couldn't reach the server." });
        return;
      }
      const mapped = appActionError(err, "Couldn't start the update. Try again in a moment.");
      notifications.show({ color: "red", title: mapped.title, message: mapped.message });
    } finally {
      setInstanceBusy(instId, false);
    }
  }

  async function loadLogs() {
    if (!id || logsLoading) return;
    const req = ++logsReqRef.current;
    setLogsLoading(true);
    try {
      const res = await apiFetch<{ logs: string }>(
        `/api/apps/instances/${id}/logs?tail=${LOGS_TAIL}`
      );
      if (req !== logsReqRef.current) return;
      setLogs(res.logs ?? "");
      setLogsError(null);
    } catch (err) {
      if (req !== logsReqRef.current) return;
      setLogs(null);
      if (!isApiError(err)) {
        setLogsError("Couldn't reach the server.");
      } else {
        setLogsError(appActionError(err, "Couldn't load logs.").message);
      }
    } finally {
      if (req === logsReqRef.current) setLogsLoading(false);
    }
  }

  /**
   * Fetch the RAW variable map from the reveal endpoint — at most once per
   * mount (cached in state). The detail response masks password-type rows
   * as "********"; the raw map is the only way to show them.
   */
  async function ensureRawVars(): Promise<Record<string, string> | null> {
    if (rawVars) return rawVars;
    if (!id) return null;
    const req = ++rawVarsReqRef.current;
    try {
      const res = await apiFetch<{ variables: Record<string, unknown> }>(
        `/api/apps/instances/${id}/variables`
      );
      if (req !== rawVarsReqRef.current) return null;
      const map: Record<string, string> = {};
      for (const [k, v] of Object.entries(res.variables ?? {})) {
        map[k] = v === null || v === undefined ? "" : String(v);
      }
      setRawVars(map);
      return map;
    } catch (err) {
      if (req === rawVarsReqRef.current) {
        notifications.show({
          color: "red",
          message: friendlyError(err, "Couldn't load the hidden value. Try again in a moment."),
        });
      }
      return null;
    }
  }

  async function toggleRevealed(key: string) {
    if (revealed.has(key)) {
      setRevealed((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      return;
    }
    // Server-masked (password-type) rows need the raw map before they can
    // show anything; on fetch failure the row stays masked.
    const row = instance?.variables.find((v) => v.key === key);
    if (row && row.value === "********" && rawVars === null) {
      const map = await ensureRawVars();
      if (!map) return;
    }
    setRevealed((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  }

  if (loading && !instance) {
    return (
      <Stack gap="xs" py="xl" align="center">
        <Loader />
        <Text c="dimmed" size="sm">
          Loading…
        </Text>
      </Stack>
    );
  }
  if (error && !instance) {
    return (
      <Stack p="md" gap="md">
        <Alert color="red" variant="light" icon={<IconAlertTriangle size={18} />}>
          {error}
        </Alert>
        <div>
          <Button variant="default" size="xs" w={140} onClick={() => navigate("/dashboard/apps")}>
            Back to list
          </Button>
        </div>
      </Stack>
    );
  }
  if (!instance) return null;

  const isBusy = busy.has(instance.id);
  const canStart = instance.status === "stopped" || instance.status === "degraded";
  const canStop = instance.status === "running" || instance.status === "degraded";
  const canRestart =
    instance.status === "running" ||
    instance.status === "degraded" ||
    instance.status === "stopped";
  const canUpdate =
    instance.status === "running" ||
    instance.status === "stopped" ||
    instance.status === "degraded";

  return (
    <Stack gap="md">
      <Anchor
        component="button"
        type="button"
        size="sm"
        onClick={() => navigate("/dashboard/apps")}
        style={{ alignSelf: "flex-start" }}
      >
        ← Back to Apps
      </Anchor>

      <Card padding="lg">
        <Group justify="space-between" align="flex-start" wrap="wrap" gap="md">
          <Stack gap={4}>
            <Group gap="sm" align="center">
              <Title order={2}>{instance.name}</Title>
              <StatusPill status={instance.status} label={appStatusCopy(instance.status)} />
            </Group>
            <Group gap="xs">
              <Text size="xs" c="dimmed">
                App <Text span fw={600}>{instance.template_name ?? instance.template_slug}</Text>
                {instance.template_commit ? ` (template ${instance.template_commit})` : ""}
              </Text>
              {instance.url && (
                <Text size="xs" c="dimmed">
                  ·{" "}
                  <Anchor href={instance.url} target="_blank" rel="noreferrer" size="xs">
                    <Group gap={2} wrap="nowrap" style={{ display: "inline-flex" }}>
                      {instance.url.replace(/^https?:\/\//, "")}
                      <IconExternalLink size={12} stroke={1.75} />
                    </Group>
                  </Anchor>
                </Text>
              )}
            </Group>
            <Text size="xs" c="dimmed">
              Created {formatDateTime(instance.created_at)} · Updated{" "}
              {formatDateTime(instance.updated_at)} · Last check{" "}
              {formatDateTime(instance.last_health_check_at)}
            </Text>
          </Stack>

          <Group gap="xs" wrap="nowrap">
            <Tooltip label={canUpdate ? "Update from the current template" : "Update is available when the app is running, stopped or needs attention"} position="bottom" withArrow>
              <Button
                size="xs"
                variant="default"
                leftSection={<IconCloudUpload size={16} stroke={1.75} />}
                disabled={!canUpdate || isBusy}
                onClick={confirmUpdate}
              >
                Update
              </Button>
            </Tooltip>
            <Tooltip label={canStart ? "Start the app" : "Start is available when the app is stopped"} position="bottom" withArrow>
              <Button
                size="xs"
                variant="default"
                leftSection={<IconPlayerPlay size={16} stroke={1.75} />}
                disabled={!canStart || isBusy}
                onClick={() => void runAction("start")}
              >
                Start
              </Button>
            </Tooltip>
            <Tooltip label={canRestart ? "Restart the app" : "Restart is available when the app is running or stopped"} position="bottom" withArrow>
              <Button
                size="xs"
                variant="default"
                leftSection={<IconRefresh size={16} stroke={1.75} />}
                disabled={!canRestart || isBusy}
                onClick={() => void runAction("restart")}
              >
                Restart
              </Button>
            </Tooltip>
            <Tooltip label={canStop ? "Stop the app" : "Stop is available when the app is running"} position="bottom" withArrow>
              <Button
                size="xs"
                variant="light"
                color="red"
                leftSection={<IconPlayerStop size={16} stroke={1.75} />}
                disabled={!canStop || isBusy}
                onClick={() => void runAction("stop")}
              >
                Stop
              </Button>
            </Tooltip>
          </Group>
        </Group>
      </Card>

      {error && (
        <Alert color="orange" variant="light" icon={<IconAlertTriangle size={18} />}>
          {error} (showing last known state)
        </Alert>
      )}

      {/* Variables */}
      <Card padding="lg">
        <Stack gap="sm">
          <Group gap="xs">
            <IconLock size={18} stroke={1.75} />
            <Title order={3}>Settings</Title>
          </Group>
          {instance.variables.length === 0 ? (
            <Text c="dimmed" size="sm">
              This app was deployed without any custom settings.
            </Text>
          ) : (
            <>
              <Text size="xs" c="dimmed">
                Values are hidden by default — use the eye icon to show one. Values are
                re-masked when you leave this page.
              </Text>
              <Table.ScrollContainer minWidth={420}>
                <Table withTableBorder>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Setting</Table.Th>
                      <Table.Th>Value</Table.Th>
                      <Table.Th w={60}>Show</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {instance.variables.map((v) => {
                      const isRevealed = revealed.has(v.key);
                      const serverMasked = v.value === "********";
                      const shown = isRevealed
                        ? serverMasked
                          ? (rawVars?.[v.key] ?? "********")
                          : v.value
                        : "••••••••";
                      return (
                        <Table.Tr key={v.key}>
                          <Table.Td>{varLabels?.[v.key] ?? v.key}</Table.Td>
                          <Table.Td>
                            <Text size="sm" ff="monospace" style={{ wordBreak: "break-all" }}>
                              {shown}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Tooltip label={isRevealed ? "Hide value" : "Show value"} withArrow>
                              <ActionIcon
                                size="md"
                                variant="default"
                                onClick={() => toggleRevealed(v.key)}
                                aria-label={isRevealed ? `Hide ${v.key}` : `Show ${v.key}`}
                              >
                                {isRevealed ? (
                                  <IconEyeOff size={16} stroke={1.75} />
                                ) : (
                                  <IconEye size={16} stroke={1.75} />
                                )}
                              </ActionIcon>
                            </Tooltip>
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            </>
          )}
        </Stack>
      </Card>

      {/* Live containers */}
      <Card padding="lg">
        <Stack gap="sm">
          <Group gap="xs">
            <IconApps size={18} stroke={1.75} />
            <Title order={3}>Containers</Title>
          </Group>
          {instance.live === null ? (
            <Text c="dimmed" size="sm">
              Live container information isn&apos;t available right now — try refreshing in a
              moment.
            </Text>
          ) : instance.live.length === 0 ? (
            <Text c="dimmed" size="sm">
              No containers are reported for this app.
            </Text>
          ) : (
            <Table.ScrollContainer minWidth={420}>
              <Table withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Service</Table.Th>
                    <Table.Th>State</Table.Th>
                    <Table.Th>Health</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {instance.live.map((entry) => (
                    <Table.Tr key={entry.service}>
                      <Table.Td ff="monospace">{entry.service}</Table.Td>
                      <Table.Td>{entry.state}</Table.Td>
                      <Table.Td>{entry.health || "—"}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </Stack>
      </Card>

      {/* Logs */}
      <Card padding="lg">
        <Stack gap="sm">
          <Group justify="space-between" wrap="nowrap">
            <Group gap="xs">
              <IconTerminal2 size={18} stroke={1.75} />
              <Title order={3}>Logs</Title>
            </Group>
            <Button
              size="xs"
              variant="default"
              leftSection={<IconRefresh size={16} stroke={1.75} />}
              loading={logsLoading}
              onClick={() => void loadLogs()}
            >
              Refresh logs
            </Button>
          </Group>
          {logsError && (
            <Alert color="red" variant="light" icon={<IconAlertTriangle size={18} />}>
              {logsError}
            </Alert>
          )}
          {!logsError && logs === null && (
            <Text c="dimmed" size="sm">
              Click “Refresh logs” to load the latest output from this app.
            </Text>
          )}
          {!logsError && logs !== null &&
            (logs.trim() === "" ? (
              <Text c="dimmed" size="sm">
                No logs to show — the app hasn&apos;t produced any output yet.
              </Text>
            ) : (
              <Code block style={{ maxHeight: 400, overflow: "auto" }}>
                {logs}
              </Code>
            ))}
        </Stack>
      </Card>

      {/* Danger zone */}
      <Card padding="lg">
        <Stack gap="sm">
          <Group gap="xs">
            <IconTrash size={18} stroke={1.75} />
            <Title order={3}>Danger zone</Title>
          </Group>
          <Text size="sm" c="dimmed">
            Deleting the app removes it from the app server, releases its web address, and
            permanently destroys all of its data and volumes.
          </Text>
          <Group>
            <Button
              variant="light"
              color="red"
              leftSection={<IconTrash size={16} stroke={1.75} />}
              loading={isBusy && instance.status === "deleting"}
              disabled={
                isBusy ||
                instance.status === "creating" ||
                instance.status === "deleting" ||
                instance.status === "updating"
              }
              onClick={confirmDelete}
            >
              Delete app
            </Button>
          </Group>
        </Stack>
      </Card>
    </Stack>
  );
}
